
import { defineComponent, markRaw, PropType } from 'vue';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import GridHeaderCellData from '@/models/grid/GridHeaderCellData';
import { useStore } from 'vuex';
import { Actions, Getters } from '@/store';
import utils from '@/utils/QSUtils';
import JWTUtils from '@/utils/JWTUtils';
import SubjectRunDataPreferences from '@/components/2.6/modals/SubjectRunDataPreferences.vue';
import ModalConfirmation from '@/components/modals/Confirmation.vue';
import LogUsageManager from '@/manager/LogUsageManager';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';
import _ from 'lodash';
import IFeedData from '@/models/comp/IFeedData';
import ProductFeaturesUtils from '@/utils/ProductFeaturesUtils';
import CompsManager from '@/manager/2.6/CompsManager';
import SmartExchangeClaimsManager from '@/manager/2.6/SmartExchangeClaimsManager';

export default defineComponent({
    components: {
        SubjectRunDataPreferences,
        ModalConfirmation,
    },
    props: {
        compsData: {
            type: Object,
            required: true,
        },
        cellData: {
            type: Object as PropType<GridHeaderCellData>,
            required: true,
        },
        sEDetails: {
            type: Object,
            required: true,
        },
        hasMLSData: {
            type: Boolean,
            required: true,
        },
        emptySourceData: {
            required: true,
        },
        isSubject: {
            type: Boolean,
            required: true,
        },
        subjectCascadeDisabled: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            modalSubjectRunDataPreferences: {
                show: false,
                component: markRaw(SubjectRunDataPreferences),
            },
            modalConfirmation: {
                show: false,
                title: '',
                titleShow: false,
                component: markRaw(ModalConfirmation),
                message: '',
                size: '',
                buttons: [] as any,
            },
            activeCompYourCount: 0,
            activeCompPeerCount: 0,
            activePhotoCount: 0,
        };
    },
    setup() {
        const store = useStore();
        return { store };
    },
    mounted() {
        (this as any).eventBus.on('resetGridHeaderElements', (e: any) => {
            this.resetGridHeaderElements();
        });
    },
    async updated() {
        await this.getPhotoViewCount();
    },
    computed: {
        isFieldData(): boolean {
            return this.cellData.initials === SourceDataInitials.FieldData;
        },
        isTransferData(): boolean {
            return this.cellData.initials === SourceDataInitials.TransferData;
        },
        isCascadeData(): boolean {
            return this.cellData.initials === SourceDataInitials.CascadeData;
        },
        getBadgeClass(): string {
            return `initial-badge-${this.cellData.initials}`;
        },
    },
    methods: {
        async getPhotoViewCount() {
            this.activePhotoCount = await CompsManager.getActiveCompPhotoCount();
        },
        photoView() {
            if (this.activePhotoCount > 0) {
                (this as any).eventBus.emit('modalPhotoView');
            } else {
                this.modalConfirmation.show = true;
                this.modalConfirmation.message = `This comp has no property images.`;
            }
        },
        showDataSources() {
            (this as any).eventBus.emit('launchDataSourcesModal');
        },
        showMapView() {
            (this as any).eventBus.emit('launchMapViewModal');
        },
        showFactSheet() {
            (this as any).eventBus.emit('launchFactSheet');
        },
        saleHistory() {
            if (ProductFeaturesUtils.isSmartDataEnabled()) {
                (this as any).eventBus.emit('launchSaleHistory');
            } else {
                this.modalConfirmation.show = true;
                this.modalConfirmation.message = `Sale History is coming soon`;
            }
        },
        useAllData(initials: string) {
            this.store.dispatch(Actions.CompAddresses.UseAllData, initials);
            LogUsageManager.useAllData(initials);
        },
        useAllSubCompData(initials: string, subCompIdx: number) {
            this.store.dispatch(Actions.CompAddresses.UseAllSubCompData, {
                initials: initials,
                subCompIdx: subCompIdx - 2, // Minus the offset of 2 so that it lines up with its subcomp data array
            });
        },
        showAdditionalFields() {
            (this as any).eventBus.emit('launchAdditionalFieldsModal');
        },
        launchMLSImportModal() {
            (this as any).eventBus.emit('modalMLSImport');
        },
        launchUnlockPeerDataModal() {
            if (!this.sEDetails.isOptedIn) {
                if (this.sEDetails.statusCode === 402) {
                    this.modalConfirmation.show = true;
                    this.modalConfirmation.title = 'Unlock Peer Data';
                    this.modalConfirmation.titleShow = true;
                    this.modalConfirmation.message = 'SmartExchange is free to join and use within QuickSource, but you may incur charges for using peer comp data outside of QuickSource. Please visit MyAccount to set up your payment information, then click "Unlock" again.';
                    this.modalConfirmation.buttons = [
                        {
                            label: 'My Account',
                            css: 'btn-primary',
                            callback: async () => {
                                const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
                                const sePaymentURL = webEnvConfig.urls?.smartExchangePaymentUrl;
                                const jwt = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken) || '';
                                const url = `${sePaymentURL}?customerNumber=${JWTUtils.getCustomerNumber(jwt)}`;
                                const optInWindow = window.open(url, '_blank', 'toolbar=yes,scrollbars=yes,resizable=yes,top=10,left=10,width=975,height=570');
                                let popupTimer = setInterval(async function () {
                                    if (optInWindow?.closed) {
                                        clearInterval(popupTimer);
                                    }
                                }, 500);
                            },
                        },
                        {
                            label: 'Close',
                            css: 'btn-outline-secondary',
                        },
                    ];
                } else if (this.sEDetails.optInUrl) {
                    const optInWindow = window.open(this.sEDetails.optInUrl, '_blank', 'toolbar=yes,scrollbars=yes,resizable=yes,top=10,left=10,width=975,height=570');
                    let popupTimer = setInterval(async () => {
                        if (optInWindow?.closed) {
                            clearInterval(popupTimer);
                            await SmartExchangeClaimsManager.getClaims();
                            this.store.dispatch(Actions.CompAddresses.ReFetchCompDataFeeds, {
                                comp: this.compsData,
                                refetch: ['se'],
                            });
                        }
                    }, 500);
                } else {
                    this.modalConfirmation.show = true;
                    this.modalConfirmation.message = `Error: Redirect url not set.`;
                }
            }
        },
        isSourceDataEmpty(initials: string) {
            if (utils.isValueInArray(this.emptySourceData, initials.toUpperCase())) {
                return true;
            }
            return false;
        },
        launchSubjectRunDataPreferencesModal() {
            this.modalSubjectRunDataPreferences.show = true;
        },
        subjectCascadeIcon(status: boolean) {
            return status ? 'fas fa-lock' : 'fas fa-lock-open';
        },
        getFieldFillerCount() {
            return this.store.getters[Getters.CompAddresses.ActiveCompFieldFillerCount];
        },
        getActiveCompSourceCountValueByPropertyName(initials: string, getProperty: string) {
            const feedDatas: IFeedData[] = this.store.getters[Getters.CompAddresses.ActiveCompSourceCounts];
            const idx = feedDatas.findIndex((f: any) => f.initials === initials);
            if (idx !== -1) {
                const value = feedDatas[idx][getProperty as keyof IFeedData];
                return `${value}`;
            }
            return '';
        },
        /**
         * This is for SE comps and subcomps only. DB subcomps are always 'Your Comp' and we don't display it.
         */
        getWhoseComp(cellData: GridHeaderCellData): string {
            const feedDatas: IFeedData[] = this.store.getters[Getters.CompAddresses.ActiveCompSourceCounts];
            const idx = feedDatas.findIndex((f: any) => f.initials === cellData.initials);
            if (idx >= 0) {
                const feedData = feedDatas[idx];
                if (cellData.subCompIdx) {
                    // Is a sub comp, use the index and counts, subcomp index begins at 2
                    if (feedData.yourCount && cellData.subCompIdx - 1 < feedData.yourCount) {
                        return 'Your Comp';
                    }
                    return 'Peer Comp';
                } else if (feedData.isPeerComp !== undefined) {
                    // This is the comp, use the feed data flag
                    return feedData.isPeerComp ? 'Peer Comp' : 'Your Comp';
                }
            }
            return '';
        },
        isSubComp(cellData: GridHeaderCellData): boolean {
            if (cellData.initials === SourceDataInitials.SmartExchange || cellData.initials === SourceDataInitials.Database) {
                return cellData.subCompIdx && cellData.subCompIdx !== null ? true : false;
            }
            return false;
        },
        getSourceCountData(initials: string, getProperty: string) {
            const result = this.getActiveCompSourceCountValueByPropertyName(initials, getProperty);
            return result ? result : 0;
        },
        resetGridHeaderElements() {
            this.resetGridShowButtons();
        },
        resetGridShowButtons() {
            // This gets call when a user switches comp tabs
            const collection = document.getElementsByClassName('sub-comp-button') as unknown as HTMLCollection;
            Array.from(collection).forEach(function (element) {
                element.setAttribute('is-collapsed', 'true');
                element.innerHTML = 'Show <i class="fas fa-angle-double-right"></i>';
            });
        },
        toggleSubCompData(event: any, initials: string) {
            let element = event.target;
            let attrValue = element.getAttribute('is-collapsed');
            if (attrValue === null) {
                element = element.parentNode;
                attrValue = element.getAttribute('is-collapsed');
            }

            let isCollapsed = utils.stringToBoolean(attrValue);
            let btnText = '';
            // Toggle collasped state
            if (isCollapsed) {
                isCollapsed = false;
                btnText = 'Hide <i class="fas fa-angle-double-left"></i>';
            } else {
                isCollapsed = true;
                btnText = 'Show <i class="fas fa-angle-double-right"></i>';
            }

            element.setAttribute('is-collapsed', isCollapsed);
            element.innerHTML = btnText;

            (this as any).eventBus.emit('toggleSubCompData', {
                initials: initials,
                isCollapsed: isCollapsed,
            });
        },
    },
});
